import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, TriangleAlert, Undo2 } from 'lucide-react';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { formatFileSize, formatTaka } from '@/utils/number';
import { pcmpProposalDetailQueryOptions } from '../../api/pcmpProposals.api';
import {
  PCMP_CHANNEL_LABELS,
  PCMP_MKT_COMMENT_MAX,
  PCMP_MKT_PROOF_ACCEPT,
  PCMP_MKT_PROOF_MAX_BYTES,
} from '../../constants/pcmpProposal.constants';
import { useReturnPcmpPayment } from '../../hooks/useReturnPcmpPayment';
import type { PcmpChannel } from '../../types/pcmpProposal.types';
import { pcmpAmountSchema } from '../../utils/pcmpProposalEditForm';
import { pcmpSegmentClass } from '../../utils/pcmpSegmentClass';
import { pcmpUploadFileSchema } from '../../utils/pcmpUploadFile';
import { PcmpCommentField } from '../PcmpMarketingActions/PcmpCommentField';
import { PcmpEditDialog } from '../PcmpProposalEdit/PcmpEditDialog';

const RETURN_MODES = ['FULL', 'PARTIAL'] as const;
type ReturnMode = (typeof RETURN_MODES)[number];

const MODE_LABELS = { FULL: 'Full', PARTIAL: 'Partial' } as const satisfies Record<
  ReturnMode,
  string
>;

const partialAmountSchema = pcmpAmountSchema('Amount', { positive: true });

/**
 * An Accounts return needs the reason and the deposit slip; a Marketing one
 * takes both as optional (LDS-766). The amount is capped at what is left.
 */
function returnFormSchema(channel: PcmpChannel, returnable: number) {
  const isAccounts = channel === 'ACCOUNTS';
  return z
    .object({
      mode: z.enum(RETURN_MODES),
      amount: z.string(),
      reason: z.string().trim().max(PCMP_MKT_COMMENT_MAX),
      slip: pcmpUploadFileSchema,
    })
    .superRefine((values, ctx) => {
      if (values.mode === 'PARTIAL') {
        const parsed = partialAmountSchema.safeParse(values.amount);
        if (!parsed.success) {
          ctx.addIssue({
            code: 'custom',
            path: ['amount'],
            message: parsed.error.issues[0]?.message ?? 'Enter a valid amount.',
          });
        } else if (Number(parsed.data) > returnable) {
          ctx.addIssue({
            code: 'custom',
            path: ['amount'],
            message: `Cannot return more than what is left (${formatTaka(returnable)}).`,
          });
        }
      }
      if (isAccounts && values.reason.trim() === '') {
        ctx.addIssue({ code: 'custom', path: ['reason'], message: 'A reason is required.' });
      }
      if (isAccounts && values.slip === null) {
        ctx.addIssue({
          code: 'custom',
          path: ['slip'],
          message: 'A bank deposit slip is required.',
        });
      }
    });
}

type ReturnFormInput = z.input<ReturnType<typeof returnFormSchema>>;
type ReturnFormValues = z.output<ReturnType<typeof returnFormSchema>>;

export interface PcmpReturnPaymentTarget {
  proposalId: number;
  refNo: string;
  /** The lane that paid it out, and so the one it comes back through. */
  channel: PcmpChannel;
}

interface PcmpReturnPaymentDialogProps {
  target: PcmpReturnPaymentTarget;
  onClose: () => void;
}

/**
 * Legacy AccountsReturn (LDS-765) and MktReturn (LDS-766): all of what is
 * left comes back and closes the proposal, or part of it and the proposal
 * stays Disbursed, to be returned again.
 */
export function PcmpReturnPaymentDialog({ target, onClose }: PcmpReturnPaymentDialogProps) {
  const { proposalId, channel } = target;
  const isAccounts = channel === 'ACCOUNTS';
  // Queue rows carry no amounts: the details say what is left to return.
  const { data: proposal, isLoading } = useQuery(pcmpProposalDetailQueryOptions(proposalId));
  const payment = proposal?.payment;
  const returnable = payment?.returnableAmount ?? 0;

  const schema = useMemo(() => returnFormSchema(channel, returnable), [channel, returnable]);
  const form = useForm<ReturnFormInput, unknown, ReturnFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { mode: 'FULL', amount: '', reason: '', slip: null },
  });
  const mode = form.watch('mode');
  const { mutate, isPending, error } = useReturnPcmpPayment(proposalId, channel, onClose);

  const onSubmit = form.handleSubmit((values) =>
    mutate({
      amount: values.mode === 'PARTIAL' ? Number(values.amount) : undefined,
      reason: values.reason || undefined,
      slip: values.slip,
    }),
  );

  const optional = isAccounts ? null : <span className="text-muted-foreground"> (optional)</span>;
  const required = isAccounts ? <span className="text-destructive"> *</span> : null;

  return (
    <Form {...form}>
      <PcmpEditDialog
        open
        onOpenChange={(open) => !open && onClose()}
        title={`Return ${PCMP_CHANNEL_LABELS[channel]} Payment`}
        description={target.refNo}
        icon={Undo2}
        isDirty={form.formState.isDirty}
        isPending={isPending}
        error={error}
        onSubmit={(event) => void onSubmit(event)}
        submitLabel={mode === 'FULL' ? 'Return all' : 'Return part'}
        submitVariant="destructive"
        submitDisabled={isLoading || returnable <= 0}
        pendingLabel="Returning..."
      >
        {isLoading || !payment ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" aria-hidden />
            Loading the payment...
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-3 rounded-lg border bg-muted/40 px-4 py-3 text-sm">
            <div className="flex flex-col">
              <span className="text-xs text-muted-foreground">Paid</span>
              <span className="font-medium">{formatTaka(payment.paidAmount)}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-xs text-muted-foreground">Returned so far</span>
              <span className="font-medium">{formatTaka(payment.returnedAmount)}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-xs text-muted-foreground">Left to return</span>
              <span className="font-medium">{formatTaka(returnable)}</span>
            </div>
          </div>
        )}

        <FormField
          control={form.control}
          name="mode"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Return</FormLabel>
              <div
                role="radiogroup"
                aria-label="Full or partial return"
                className="flex w-fit items-center rounded-lg border bg-muted/40 p-1"
              >
                {RETURN_MODES.map((value) => (
                  <Button
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={field.value === value}
                    variant="ghost"
                    size="sm"
                    onClick={() => field.onChange(value)}
                    className={pcmpSegmentClass(field.value === value)}
                  >
                    {MODE_LABELS[value]}
                  </Button>
                ))}
              </div>
            </FormItem>
          )}
        />

        {mode === 'FULL' ? (
          <div className="flex gap-2 rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm">
            <TriangleAlert className="mt-0.5 size-4 shrink-0 text-destructive" aria-hidden />
            <span>
              Everything left ({formatTaka(returnable)}) comes back and the proposal is closed for
              good. No further action can be taken on it.
            </span>
          </div>
        ) : (
          <FormField
            control={form.control}
            name="amount"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Amount<span className="text-destructive"> *</span>
                </FormLabel>
                <FormControl>
                  <Input {...field} inputMode="decimal" autoComplete="off" placeholder="0.00" />
                </FormControl>
                <FormDescription>
                  Up to {formatTaka(returnable)}. The proposal stays Disbursed and can be returned
                  again.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <FormField
          control={form.control}
          name="slip"
          render={({ field: { value, onChange, onBlur, name, ref } }) => (
            <FormItem>
              <FormLabel>
                Bank deposit slip
                {required}
                {optional}
              </FormLabel>
              <FormControl>
                <Input
                  ref={ref}
                  name={name}
                  type="file"
                  accept={PCMP_MKT_PROOF_ACCEPT}
                  onBlur={onBlur}
                  onChange={(event) => onChange(event.target.files?.[0] ?? null)}
                  className="cursor-pointer"
                />
              </FormControl>
              <FormDescription>
                {value
                  ? `${value.name} · ${formatFileSize(value.size)}`
                  : `JPG, PNG or PDF, up to ${formatFileSize(PCMP_MKT_PROOF_MAX_BYTES)}.`}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <PcmpCommentField
          control={form.control}
          name="reason"
          label="Remarks"
          required={isAccounts}
          placeholder={isAccounts ? 'Why the money came back' : 'Optional'}
          description="Recorded on the proposal's timeline."
        />
      </PcmpEditDialog>
    </Form>
  );
}
